import React, { useCallback } from "react";
import { useEffect, useMemo, useState } from "react";
import {
    IStackStyles,
    Stack,
    Link
} from "@fluentui/react";
import { CustomFontText } from "components/customFontText/CustomFontText";
import { Col, Row } from "react-grid-system";
import {
    useSelector,
    useDispatch
} from "react-redux";
import { AppState } from "store/index";
import { isKarlProfile } from "utils/permissions/permissionsHelpers";
import { handleGetLocations } from "store/locations/locations.action";
import { handleGetFiScorecard, handleGetFiScorecardFinancePersons, handlePrintFiScorecard } from "store/dealTracker/dealTracker.action";
import { TreeTable } from 'primereact/treetable';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { classNames } from "primereact/utils";
import { currencyNegValFractionalFormat, numberFormat } from "utils/helperFunctions";
import moment from "moment";
import { fiReportAfterSalesOptions, fiReportDateRangeOptions, fiReportFleetStatusOptions, fiReportNewUsedOptions } from "constants/constants";

const container: IStackStyles = {
    root: {
        marginTop: 10,
        minHeight: 500
    },
};


// The API sends "--" for metrics that don't apply to a row (e.g. a
// single-product category's penetration); pass those through as-is.
const formatCurrency = (val: any) => {
    if (val === undefined || val === null || val === '') {
        return '';
    }
    return typeof val === 'number' ? currencyNegValFractionalFormat(val) : String(val);
};
const formatNumber = (val: any) => {
    if (val === undefined || val === null || val === '') {
        return '';
    }
    return typeof val === 'number' ? numberFormat(val) : String(val);
};
const formatUnits = (val: any) => {
    if (val === undefined || val === null || val === '') {
        return '';
    }
    return typeof val === 'number' ? val.toFixed(1) : String(val);
};
const formatPercent = (val: any, digits = 2) => {
    if (val === undefined || val === null || val === '') {
        return '';
    }
    return typeof val === 'number' ? `${val.toFixed(digits)}%` : String(val);
};

// Sizes every column in an exported worksheet to fit its widest cell,
// so the Excel output doesn't need a manually tuned column width per report.
const autoFitExcelColumns = (worksheet: any) => {
    const colWidths: number[] = [];
    worksheet.eachRow({ includeEmpty: true }, (row: any) => {
        row.eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => {
            const cellText = cell.value === undefined || cell.value === null ? '' : String(cell.value);
            colWidths[colNumber] = Math.max(colWidths[colNumber] || 10, cellText.length + 2);
        });
    });
    colWidths.forEach((width, colNumber) => {
        if (colNumber) {
            worksheet.getColumn(colNumber).width = width;
        }
    });
};

export const FinanceScorecardNewReport = () => {
    const dispatch = useDispatch();

    const user = useSelector((state: AppState) => state.userProfile.userProfile) as any;
    const locations = useSelector((state: AppState) => state.locations.locations) as any;
    const isKarlProfileType = user.profileType && isKarlProfile(user.profileType);

    const [locationList, setLocationList] = useState([]);
    const [financePersonList, setFinancePersonList] = useState([]);

    const [dateRangeFilter, setDateRangeFilter] = useState('currentMonth');
    const [fromDate, setFromDate] = useState<Date>(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState<Date>(() => new Date());
    const [locationFilter, setLocationFilter] = useState<number[]>(user?.primaryLocation?.id ? [user.primaryLocation.id] : []);
    const [financePersonFilter, setFinancePersonFilter] = useState<number[]>([]);
    const [conditionFilter, setConditionFilter] = useState<string[]>(['New', 'Used']);
    const [fleetStatusFilter, setFleetStatusFilter] = useState<string[]>(['No']);
    const [afterSalesFilter, setAfterSalesFilter] = useState<string[]>(['current', 'afterSales']);
    const [fiScorecardData, setFiScorecardData] = useState<any>(null);
    const [commonServerError, setcommonServerError] = useState(null);

    const ExcelJS = require('exceljs');
    const { saveAs } = require('file-saver');


    useEffect(() => {
        dispatch<any>(handleGetLocations());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (locations && locations.results && locations.results.length) {
            const list = [...locations.results];
            setLocationList(list);
            setLocationFilter(user?.primaryLocation?.id ? [user.primaryLocation.id] : (list[0]?.id ? [list[0].id] : []));
        }
    }, [locations, user?.primaryLocation?.id]);

    // GET /reports/fiScorecardFinancePersons?fromDate=&toDate=&locationId=
    useEffect(() => {
        let isCancelled = false;

        const fetchFinancePersons = async () => {
            const formData: any = {
                fromDate: moment(fromDate).format('DD-MM-YYYY'),
                toDate: moment(toDate).format('DD-MM-YYYY'),
            };

            if (locationFilter.length) {
                formData.locationId = locationFilter.join(',');
            }

            try {
                await dispatch<any>(
                    handleGetFiScorecardFinancePersons({
                        formData,
                        callback: (res) => {
                            // Ignore response if filters have already changed
                            if (isCancelled) return;

                            if (
                                Array.isArray(res?.financePersons) &&
                                res.financePersons.length
                            ) {
                                const list = res.financePersons.map((item: any) => ({
                                    key: item.id,
                                    text: `${item.firstName} ${item.lastName}`,
                                }));

                                setFinancePersonList(list);
                                setFinancePersonFilter(
                                    list.map((item: any) => item.key)
                                );
                            } else {
                                setFinancePersonList([]);
                                setFinancePersonFilter([]);
                            }
                        },
                    })
                );
            } catch (error) {
                if (!isCancelled) {
                    setFinancePersonList([]);
                    setFinancePersonFilter([]);
                }
            }
        };

        fetchFinancePersons();

        return () => {
            isCancelled = true;
        };
    }, [fromDate, toDate, locationFilter, dispatch]);

    // Shared query mapping for both GET /reports/fiScorecard and its /pdf export.
    const buildFiScorecardFormData = useCallback(() => {
        const formData: Record<string, string> = {
            fromDate: moment(fromDate).format('DD-MM-YYYY'),
            toDate: moment(toDate).format('DD-MM-YYYY'),
        };

        if (locationFilter.length > 0) {
            formData.primaryLocationId = locationFilter.join(',');
        }

        if (conditionFilter.length > 0) {
            formData.newUsed = conditionFilter
                .map((value) => value.toLowerCase())
                .join(',');
        }

        if (financePersonFilter.length > 0) {
            formData.financePerson = financePersonFilter.join(',');
        }

        if (fleetStatusFilter.length > 0) {
            formData.fleet = fleetStatusFilter
                .map((value) => value.toLowerCase())
                .join(',');
        }

        if (afterSalesFilter.length > 0) {
            formData.includeAfterSales = afterSalesFilter.join(',');
        }

        return formData;
    }, [locationFilter, conditionFilter, financePersonFilter, fleetStatusFilter, afterSalesFilter, fromDate, toDate]);

    const fetchFiScorecardData = useCallback(async() => {
        const formData = buildFiScorecardFormData();

        await dispatch<any>(
            handleGetFiScorecard({
                formData,
                callback: (res) => {
                    if (res) {
                        setFiScorecardData(res);
                    } else {
                        setFiScorecardData(null);
                        setcommonServerError('Failed to Fetch the Data.');
                    }
                },
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildFiScorecardFormData]);

    useEffect(() => {
        setcommonServerError(null);
        fetchFiScorecardData();
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        fromDate,
        toDate,
        locationFilter,
        conditionFilter,
        financePersonFilter,
        fleetStatusFilter,
        afterSalesFilter,
    ]);
    const applyDateRangePreset = (key: string) => {
        const today = moment();
        let from = moment().startOf('month');
        let to = today;
        if (key === 'last6Months') {
            // Last 6 complete months, excluding the current (in-progress) month.
            from = moment().subtract(6, 'months').startOf('month');
            to = moment().subtract(1, 'month').endOf('month');
        } else if (key === 'currentYearToDate') {
            from = moment().startOf('year');
        }
        setDateRangeFilter(key);
        setFromDate(from.toDate());
        setToDate(to.toDate());
    };

    const onLocationChange = (e) => setLocationFilter(e.value);
    const onFinancePersonChange = (e) => setFinancePersonFilter(e.value);
    const onConditionChange = (e) => setConditionFilter(e.value);
    const onFleetStatusChange = (e) => setFleetStatusFilter(e.value);
    const onAfterSalesChange = (e) => setAfterSalesFilter(e.value);

    // Deal Status/Finance Type/Payment Type rows come pre-counted and
    // pre-percentaged from the API — no client-side aggregation needed.
    const dealStatusRows = useMemo(() => {
        const ds = fiScorecardData?.dealStatus;
        if (!ds) {
            return [];
        }
        return [
            { label: 'Closed', value: ds.closed?.count, percentage: ds.closed?.percentage },
            { label: 'Void', value: ds.void?.count, percentage: ds.void?.percentage },
        ];
    }, [fiScorecardData]);
    const dealStatusTotal = fiScorecardData?.dealStatus?.total;

    const financeTypeRows = useMemo(() => {
        const ft = fiScorecardData?.financeType;
        if (!ft) {
            return [];
        }
        return [
            { label: 'Retail', value: ft.retail?.count, percentage: ft.retail?.percentage },
            { label: 'Lease', value: ft.lease?.count, percentage: ft.lease?.percentage },
        ];
    }, [fiScorecardData]);
    const financeTypeTotal = fiScorecardData?.financeType?.total;

    const paymentTypeRows = useMemo(() => {
        const pt = fiScorecardData?.paymentType;
        if (!pt) {
            return [];
        }
        return [
            { label: 'Cash', value: pt.cash?.count, percentage: pt.cash?.percentage },
            { label: 'Financed', value: pt.financed?.count, percentage: pt.financed?.percentage },
        ];
    }, [fiScorecardData]);
    const paymentTypeTotal = fiScorecardData?.paymentType?.total;

    const perCarValue = fiScorecardData?.perCar;
    const productIndexValue = fiScorecardData?.productIndex;

    const productCategoryTree = useMemo(() => {
        const categories = fiScorecardData?.productCategories;
        if (!Array.isArray(categories) || !categories.length) {
            return [];
        }
        const nodes = categories.map((cat: any, index) => {
            const children = (cat.products || []).map((prod: any, i) => ({
                key: `${index}-${i}`,
                data: {
                    category: '',
                    product: prod.financeProductName,
                    rowType: 'Product',
                    productUnits: prod.productUnits,
                    total: prod.totalAmount,
                    perUnit: prod.perUnitAmount,
                    penetration: prod.penetration,
                }
            }));
            return {
                key: `${index}`,
                data: {
                    category: cat.productCategory,
                    product: '',
                    rowType: 'Category',
                    productUnits: cat.categoryUnits,
                    total: cat.categoryAmount,
                    perUnit: cat.categoryPerUnit,
                    penetration: cat.categoryPenetration,
                },
                children,
            };
        });

        nodes.push({
            key: 'total',
            data: {
                category: 'Total',
                product: '',
                rowType: 'Total',
                productUnits: fiScorecardData?.totals?.totalUnits,
                total: fiScorecardData?.totals?.totalAmount,
                perUnit: undefined,
                penetration: undefined,
            }
        } as any);

        return nodes;
    }, [fiScorecardData]);

    const categoryBodyTemplate = (rowData) => rowData.data.rowType !== 'Product' ? rowData.data.category : '';
    const productBodyTemplate = (rowData) => rowData.data.rowType === 'Product' ? rowData.data.product : '';

    const togglerTemplate = (node, options) => {
        if (!node) {
            return;
        }
        const expanded = options.expanded;
        const iconClassName = classNames('p-treetable-toggler-icon pi pi-fw', {
            'pi-plus-circle': !expanded,
            'pi-minus-circle': expanded
        });
        // Every row shows the expand/collapse icon except the pinned Total row.
        const buttonStyle = { ...options.buttonStyle, visibility: node.key === 'total' ? 'hidden' : 'visible' as const };
        return (
            <button type="button" className="treetablebuttonStyle p-treetable-toggler p-link" style={{ ...buttonStyle, color: '#ffffff', backgroundColor: '#0065cb' }} tabIndex={-1} onClick={options.onClick}>
                <span className={iconClassName} aria-hidden="true"></span>
            </button>
        );
    };

    const rowClassName = (node) => {
        if (node.key === 'total') {
            return { fiScorecardTotalRow: true };
        }
        return { childRow: (node.key && node.key.includes('-')) };
    };

    // Keeps the synthetic Total row pinned as the last row no matter which
    // column/direction the Product Category table is sorted by.
    const pinTotalRowSort = (event: { data: any[]; field: string; order: number }) => {
        const rows = [...event.data];
        const totalIndex = rows.findIndex((row: any) => row.key === 'total');
        const totalRow = totalIndex > -1 ? rows.splice(totalIndex, 1)[0] : null;
        rows.sort((a: any, b: any) => {
            const valA = a.data ? a.data[event.field] : null;
            const valB = b.data ? b.data[event.field] : null;
            if (valA === valB) {
                return 0;
            }
            if (valA === undefined || valA === null) {
                return 1;
            }
            if (valB === undefined || valB === null) {
                return -1;
            }
            const result = typeof valA === 'string' ? valA.localeCompare(valB) : (valA < valB ? -1 : 1);
            return event.order * result;
        });
        if (totalRow) {
            rows.push(totalRow);
        }
        return rows;
    };

    const renderCountSummaryCard = (title: string, unitsLabel: string, rows: { label: string; value: number; percentage: number }[], total?: number) => (
        <div className="fiScorecardSummaryCard">
            <div className="fiScorecardSummaryHeader">{title}</div>
            <DataTable value={rows} className="fiScorecardSummaryTable" paginator={false}>
                <Column field="label" header={title} sortable />
                <Column field="value" header={unitsLabel} body={(row) => formatUnits(row.value)} style={{ width: '70px' }} sortable />
                <Column field="percentage" header="%" body={(row) => formatPercent(row.percentage)} style={{ width: '70px' }} sortable />
            </DataTable>
            <div className="fiScorecardSummaryDivider" />
            <div className="fiScorecardSummaryRow fiScorecardTotalRowInline">
                <span>Total</span>
                <span>{formatUnits(total)}</span>
                <span></span>
            </div>
        </div>
    );

    const exportData = () => {
        const workbook = new ExcelJS.Workbook();

        const dealStatusSheet = workbook.addWorksheet('Deal Status - Display');
        dealStatusSheet.addRow(['Deal Status', 'Total Deals', '%']);
        dealStatusRows.forEach((r) => dealStatusSheet.addRow([r.label, r.value, r.percentage]));
        dealStatusSheet.addRow(['Total', dealStatusTotal, null]);
        autoFitExcelColumns(dealStatusSheet);

        const financeTypeSheet = workbook.addWorksheet('Finance Type - Display');
        financeTypeSheet.addRow(['Finance Type', 'Closed Deals', '%']);
        financeTypeRows.forEach((r) => financeTypeSheet.addRow([r.label, r.value, r.percentage]));
        financeTypeSheet.addRow(['Total', financeTypeTotal, null]);
        autoFitExcelColumns(financeTypeSheet);

        const paymentTypeSheet = workbook.addWorksheet('Payment Type - Display');
        paymentTypeSheet.addRow(['Payment Type', 'Closed Deals', '%']);
        paymentTypeRows.forEach((r) => paymentTypeSheet.addRow([r.label, r.value, r.percentage]));
        paymentTypeSheet.addRow(['Total', paymentTypeTotal, null]);
        autoFitExcelColumns(paymentTypeSheet);

        const productSheet = workbook.addWorksheet('Details Table - Display');
        productSheet.addRow(['Product Category', 'Product', 'Product Units', 'Total $', 'Per Unit $', '% Penetration']);
        productCategoryTree.forEach((cat: any) => {
            productSheet.addRow([cat.data.category, cat.data.product, cat.data.productUnits, cat.data.total, cat.data.perUnit, cat.data.penetration]);
            (cat.children || []).forEach((child: any) => {
                productSheet.addRow([child.data.category, child.data.product, child.data.productUnits, child.data.total, child.data.perUnit, child.data.penetration]);
            });
        });
        autoFitExcelColumns(productSheet);

        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'F&I Scorecard LIVE_Export.xlsx');
        });
    };

    // GET /reports/fiScorecard/pdf?fromDate=&toDate=&primaryLocationId=&newUsed=&financePerson=&fleet=&includeAfterSales=
    const exportPdf = () => {
        dispatch<any>(handlePrintFiScorecard({
            formData: buildFiScorecardFormData(),
            callback: (response) => {
                // clientPrintPdf opens the PDF in a new tab itself and resolves to
                // { data, error }; data is only set when the PDF was generated.
                if (response?.data) {
                    setcommonServerError(null);
                } else {
                    setcommonServerError('Failed to print the pdf');
                }
            }
        }));
    };

    return (

        isKarlProfileType ? <>
            <div className="reportFinanceProductWidth fiScorecardNewReport">
                <Stack styles={container} tokens={{ childrenGap: 10 }}>

                    <Stack
                        horizontal
                        verticalAlign="center"
                        horizontalAlign="space-between"
                        className="loc_btn_par_con"
                    >
                        <Row>
                            <Col className="alignHeaders">
                                <CustomFontText className="pageTitleFont pageName pageHeaderFont">F&I Scorecard</CustomFontText>
                            </Col>
                        </Row>
                    </Stack>
                    {
                        commonServerError ?
                            <div>
                                <p style={{ marginBottom: 0, padding: 0 }} className="genericErrorColor"><span>{commonServerError}</span></p>
                            </div> : null
                    }
                    <div className="table-header" style={{ marginBottom: 10 }}>
                        <div className="fiScorecardFilterRow">
                            {fiReportDateRangeOptions.map((opt, index) => (
                                <div className="fiScorecardFilterCell" key={opt.key}>
                                    <label className="lbl-text">{index === 0 ? 'Date Range :' : ' '}</label>
                                    <button
                                        type="button"
                                        className={classNames('fiScorecardDateBtn', { active: dateRangeFilter === opt.key })}
                                        onClick={() => applyDateRangePreset(opt.key)}
                                    >
                                        {opt.text}
                                    </button>
                                </div>
                            ))}
                            <div className="fiScorecardFilterCell">
                                <div className="ms-TextField-wrapper">
                                    <label className="lbl-text">From :</label>
                                    <div className="ms-TextField-fieldGroup mask-FieldGroup date-filt date-filter">
                                        <Calendar
                                            id="fiFromDate"
                                            style={{ width: '100%' }}
                                            value={fromDate}
                                            showIcon
                                            onChange={(e) => {
                                                setFromDate(e.value as Date);
                                                setDateRangeFilter('');
                                            }}
                                            monthNavigator
                                            yearNavigator
                                            yearRange="2000:2100"
                                            maxDate={toDate}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="fiScorecardFilterCell">
                                <div className="ms-TextField-wrapper">
                                    <label className="lbl-text">To :</label>
                                    <div className="ms-TextField-fieldGroup mask-FieldGroup date-filt date-filter">
                                        <Calendar
                                            id="fiToDate"
                                            style={{ width: '100%' }}
                                            value={toDate}
                                            showIcon
                                            onChange={(e) => {
                                                setToDate(e.value as Date);
                                                setDateRangeFilter('');
                                            }}
                                            monthNavigator
                                            yearNavigator
                                            yearRange="2000:2100"
                                            minDate={fromDate}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="fiScorecardFilterRow" style={{ marginTop: 10 }}>
                            <div className="fiScorecardFilterCell">
                                <label className="lbl-text">Deal Location :</label>
                                <MultiSelect
                                    id="fiLocation"
                                    value={locationFilter}
                                    options={locationList}
                                    onChange={onLocationChange}
                                    optionLabel="legalName"
                                    optionValue="id"
                                    appendTo="self"
                                    className="carlReportmultiselect custom-p-dropdown write-ups-location-dd"
                                    maxSelectedLabels={1}
                                    selectAllLabel="Select All"
                                    selectedItemsLabel="{0} selected"
                                    filter={false}
                                />
                            </div>
                            <div className="fiScorecardFilterCell">
                                <label className="lbl-text">Finance Person :</label>
                                <MultiSelect
                                    id="fiFinancePerson"
                                    value={financePersonFilter}
                                    options={financePersonList}
                                    onChange={onFinancePersonChange}
                                    optionLabel="text"
                                    optionValue="key"
                                    appendTo="self"
                                    className="carlReportmultiselect custom-p-dropdown write-ups-location-dd"
                                    maxSelectedLabels={1}
                                    selectAllLabel="Select All"
                                    selectedItemsLabel="{0} selected"
                                    filter={true}
                                />
                            </div>
                            <div className="fiScorecardFilterCell">
                                <label className="lbl-text">New / Used :</label>
                                <MultiSelect
                                    id="fiCondition"
                                    value={conditionFilter}
                                    options={fiReportNewUsedOptions}
                                    onChange={onConditionChange}
                                    optionLabel="text"
                                    optionValue="key"
                                    appendTo="self"
                                    className="carlReportmultiselect custom-p-dropdown write-ups-location-dd"
                                    maxSelectedLabels={1}
                                    selectAllLabel="Select All"
                                    selectedItemsLabel="{0} selected"
                                />
                            </div>
                            <div className="fiScorecardFilterCell">
                                <label className="lbl-text">Fleet Status :</label>
                                <MultiSelect
                                    id="fiFleetStatus"
                                    value={fleetStatusFilter}
                                    options={fiReportFleetStatusOptions}
                                    onChange={onFleetStatusChange}
                                    optionLabel="text"
                                    optionValue="key"
                                    appendTo="self"
                                    className="carlReportmultiselect custom-p-dropdown write-ups-location-dd"
                                    maxSelectedLabels={1}
                                    selectAllLabel="Select All"
                                    selectedItemsLabel="{0} selected"
                                />
                            </div>
                            <div className="fiScorecardFilterCell">
                                <label className="lbl-text">Include After Sales :</label>
                                <MultiSelect
                                    id="fiAfterSales"
                                    value={afterSalesFilter}
                                    options={fiReportAfterSalesOptions}
                                    onChange={onAfterSalesChange}
                                    optionLabel="text"
                                    optionValue="key"
                                    appendTo="self"
                                    className="carlReportmultiselect custom-p-dropdown write-ups-location-dd"
                                    maxSelectedLabels={1}
                                    selectAllLabel="Select All"
                                    selectedItemsLabel="{0} selected"
                                />
                            </div>
                        </div>
                    </div>
                    <Stack
                        horizontal
                        verticalAlign="end"
                        horizontalAlign="end"
                        className="loc_btn_par_con"
                    >

                        <Stack horizontal tokens={{ childrenGap: 10 }}>

                            <div>
                                <Link style={{ marginLeft: '10px' }} className="workbook-export-btn" color="inherit" onClick={exportData} data-pr-tooltip="XLS">
                                    Export Excel
                                </Link>
                            </div>
                            <div>
                                <Link className="workbook-export-btn" color="inherit" onClick={exportPdf} data-pr-tooltip="PDF">
                                    Export Pdf
                                </Link>
                            </div>
                        </Stack>
                    </Stack>

                    <div className="fiScorecardSummaryGrid">
                        {renderCountSummaryCard('Deal Status', 'Total Deals', dealStatusRows, dealStatusTotal)}
                        {renderCountSummaryCard('Finance Type', 'Closed Deals', financeTypeRows, financeTypeTotal)}
                        {renderCountSummaryCard('Payment Type', 'Closed Deals', paymentTypeRows, paymentTypeTotal)}
                        <div className="fiScorecardKpiGroup">
                            <div className="fiScorecardKpiCard">
                                <div className="fiScorecardKpiHeader">Per Car*</div>
                                <div className="fiScorecardKpiValue">{formatCurrency(perCarValue)}</div>
                            </div>
                            <div className="fiScorecardKpiCard">
                                <div className="fiScorecardKpiHeader">Product Index</div>
                                <div className="fiScorecardKpiValue">{typeof productIndexValue === 'number' ? productIndexValue.toFixed(2) : ''}</div>
                            </div>
                        </div>
                    </div>

                    <div className="datatable-filter-demo noMaxWidthTable cust-tree-tbl fiScorecardTreeTable">
                        <div className="card reportsTableVal">
                            <TreeTable
                                value={productCategoryTree}
                                selectionMode="single"
                                togglerTemplate={togglerTemplate}
                                rowClassName={rowClassName}
                            >
                                <Column field="category" header="Product Category" expander body={categoryBodyTemplate} style={{ width: '14em' }} sortable sortFunction={pinTotalRowSort} />
                                <Column field="product" header="Product" body={productBodyTemplate} style={{ width: '14em' }} />
                                <Column field="productUnits" header="Product Units" body={(rowData) => formatNumber(rowData.data.productUnits)} sortable sortFunction={pinTotalRowSort} />
                                <Column field="total" header="Total $" body={(rowData) => formatCurrency(rowData.data.total)} sortable sortFunction={pinTotalRowSort} />
                                <Column field="perUnit" header="Per Unit $" body={(rowData) => formatCurrency(rowData.data.perUnit)} sortable sortFunction={pinTotalRowSort} />
                                <Column field="penetration" header="% Penetration" body={(rowData) => formatPercent(rowData.data.penetration)} sortable sortFunction={pinTotalRowSort} />
                            </TreeTable>
                        </div>
                    </div>
                    <div className="fiScorecardFootnote">*Per Car calculation EXCLUDES commercial and wholesale deals from dollars and closed deal count.</div>
                </Stack>
            </div>
        </> : null
    );
};
